import React, { useEffect, useState } from "react";
import { useLang } from "hooks/useLang";
import SupportLayout from "components/page_components/SupportPage/SupportLayout";
import ScrollToTop from "components/ScrollToTop";
import useScrollToSection from "hooks/useScrollToSection";
import CustomMarkdown from "components/atoms/CustomMarkdown";

export const meta = {
  author: "StartHubIT",
};

const PrivacyPolicy = () => {
  const { scrollToSection } = useScrollToSection();
  const { isAr, t } = useLang();

  const navItems = [
    { id: "overview", label: "Overview" },
    { id: "purpose", label: "Purpose" },
    { id: "compliance", label: "Compliance" },
    { id: "changes", label: "Changes" },
    { id: "personal-data", label: "Personal Data" },
    { id: "no-data", label: "No Data" },
    { id: "sources", label: "Sources" },
    { id: "usage", label: "Usage" },
    { id: "retention", label: "Retention" },
    { id: "sharing", label: "Sharing" },
    { id: "changes-in-company", label: "Changes in Company" },
    { id: "data-transfer", label: "Data Transfer" },
    { id: "automated-decisions", label: "Automated Decisions" },
    { id: "analytics-ads", label: "Analytics & Ads" },
    { id: "security", label: "Security" },
    { id: "your-rights", label: "Your Rights" },
    { id: "what-we-need", label: "What We Need" },
    { id: "third-party-links", label: "Third Party Links" },
    { id: "contact-us", label: "Contact Us" },
  ];

  const SideNav = () => {
    const [isFixed, setIsFixed] = useState(true);
    const [sideNavTop, setSideNavTop] = useState("37.5%");

    // Nav is fixed until reach close to the footer then stop fixing it
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      const scrollPercentage = (scrollTop / documentHeight) * 100;

      if (scrollPercentage > 95) {
        if (isFixed) {
          const sideNavElement = document.querySelector(".sidenav");
          if (sideNavElement) {
            const offsetTop =
              sideNavElement.getBoundingClientRect().top + window.scrollY;
            setSideNavTop(`${offsetTop}px`);
          }
          setIsFixed(false);
        }
      } else {
        if (!isFixed) {
          setIsFixed(true);
          setSideNavTop("37.5%");
        }
      }
    };

    useEffect(() => {
      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }, [isFixed]);

    return (
      <div
        className={`sidenav left-0 hidden flex-col space-y-[0.5rem] md:flex w-[220px] overflow-y-auto max-h-[40%] px-6 ${
          isFixed ? "fixed" : "absolute"
        }`}
        style={{
          top: isFixed ? "37.5%" : sideNavTop,
          direction: isAr ? "rtl" : "ltr",
        }}
      >
        {navItems.map((item, index) => (
          <div
            key={index}
            onClick={() => scrollToSection(item.id)}
            className="transition-colors cursor-pointer hover:text-primary dark:hover:text-blue-400"
          >
            <span className="cursor-pointer hover:text-primary dark:hover:text-blue-400 text-sm">
              {index + 1}. {item.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <SupportLayout
      Title={t("privacy_policy_title")}
      showSearch={false}
      TitlePa="pt-[9rem]"
      forceDirectionLtr={false}
    >
      <div className="overflow-hidden px-5 md:px-[4.125rem]">
        <SideNav />
        <ScrollToTop />
        <div
          className="md:ml-[200px] mb-[7.75rem] pt-[2rem]"
          style={{ direction: isAr ? "rtl" : "ltr" }}
        >
          <div className="md:mx-8 my-20 leading-8">
            <CustomMarkdown
              content={
                isAr
                  ? `
                                
**سياسة الخصوصية والإشعار**

في باس تكيتس، نحترم خصوصيتك ونسعى جاهدين لحماية بياناتك الشخصية. يشرح هذا المستند كيفية تعاملنا مع البيانات الشخصية على مواقعنا الإلكترونية، وتطبيقاتنا، ومنصاتنا الرقمية. يجمع هذا المستند بين سياسة الخصوصية وإشعار الخصوصية لدينا لتوفير الوضوح وضمان الامتثال للقوانين المعمول بها لحماية البيانات، بما في ذلك نظام حماية البيانات الشخصية في المملكة العربية السعودية (PDPL) واللوائح ذات الصلة. باستخدام منصاتنا، فإنك توافق على شروط هذا المستند. إذا كانت لديك أي استفسارات، فلا تتردد في التواصل معنا.


**من نحن؟**

باس تكيتس ("نحن"، "خاصتنا") هي شركة مسجلة لدى وزارة التجارة في المملكة العربية السعودية. نقدم خدمات متعلقة بالأنشطة الترفيهية والسياحية داخل المملكة. بصفتنا "المتحكم في البيانات"، نحن مسؤولون عن تحديد كيفية جمع البيانات الشخصية ومعالجتها ومشاركتها، مع ضمان الامتثال للمعايير القانونية والأخلاقية.


**ما البيانات الشخصية التي نجمعها؟**

عندما تتفاعل معنا، قد نجمع بيانات شخصية مثل اسمك الكامل، ورقم هاتفك المحمول، وعنوان بريدك الإلكتروني، وجنسك، وبلدك، وجنسيتك. بناءً على تفاعلاتك، قد نجمع أيضًا بيانات الموقع بناءً على إعدادات جهازك، وتفاصيل الحجز مثل تاريخ الفعالية، وأنواع التذاكر، وعددها، وتفاصيل الدفع مثل نوع البطاقة، وتاريخ المعاملة، ووقت الدفع. كما قد نجمع بيانات التفاعل، بما في ذلك عدد مرات استخدامك للمنصة، وبيانات تقنية مثل عنوان IP الخاص بك، ونوع المتصفح، ونوع الجهاز، والمنطقة الزمنية، بالإضافة إلى تفضيلاتك الترويجية، وبيانات الاستخدام المتعلقة بنشاطك على المنصة وسجل التصفح. بالإضافة إلى ذلك، قد نجمع معلومات مثل التعليقات، وإجابات الاستبيانات، والتفضيلات، والتفاعلات مع دعم العملاء، وتحليلات لتحسين خدماتنا.

**لماذا نجمع بياناتك؟**

نجمع بياناتك الشخصية لتقديم الخدمات المطلوبة، وتحسين تجربة المستخدم، وإرسال التحديثات أو العروض الترويجية بموافقتك، والامتثال للالتزامات القانونية. هدفنا هو تقديم خدمة استثنائية مع ضمان الشفافية وحماية بياناتك.

**حقوقك**

بموجب نظام حماية البيانات الشخصية والقوانين المعمول بها، لديك الحق في الوصول إلى بياناتك، وطلب تصحيح أي معلومات غير دقيقة أو غير كاملة، وطلب حذف بياناتك عند السماح القانوني بذلك، والاعتراض على استخدام بياناتك لأغراض معينة.

**مشاركة البيانات**

قد نشارك بياناتك مع مزودي الخدمات لمعالجة المدفوعات أو تقديم الخدمات، ومع شركاء للفعاليات المشتركة أو العروض الترويجية بموافقتك

**أمان البيانات**

نستخدم تدابير تقنية وإدارية لحماية بياناتك الشخصية من الوصول غير المصرح به، أو الفقدان، أو سوء الاستخدام

**فترة الاحتفاظ بالبيانات**

نحتفظ ببياناتك فقط طالما كانت ضرورية للأغراض المحددة أو حسبما يقتضيه القانون.

**اتخاذ القرارات الآلية والتحليلات**

لا نقوم بمعالجة بياناتك باستخدام اتخاذ القرارات الآلية أو التوصيف الشخصي. ومع ذلك، نستخدم التحليلات لتحسين منصاتنا، وقد نعرض إعلانات مستهدفة بموافقتك. لمزيد من التفاصيل، يُرجى الرجوع إلى سياسة ملفات تعريف الارتباط الخاصة بنا

**الأساس القانوني لمعالجة البيانات**

نقوم بمعالجة بياناتك للوفاء بالالتزامات التعاقدية، والامتثال للمتطلبات القانونية، وتحسين خدماتنا بناءً على المصالح المشروعة.

**التغييرات في الملكية**

إذا حدث تغيير في ملكية باس تكيتس، مثل بيع أو دمج، قد يتم نقل بياناتك إلى الكيان الجديد الذي سيحترم هذه السياسة والإشعار.

**تحديثات هذا المستند**

قد يتم تحديث هذا المستند لتعكس تغييرات قانونية أو تشغيلية، وسيتم إخطارك بالتحديثات المهمة عبر البريد الإلكتروني، أو الإشعارات، أو اللافتات على منصاتنا.

**إخلاء المسؤولية**

باس تكيتس ليست مسؤولة عن أي أضرار مباشرة أو غير مباشرة، أو خسائر عرضية أو تبعية، تنشأ عن استخدامك أو عدم قدرتك على استخدام منصاتنا.

**كيفية الاتصال بنا**

لأي استفسارات أو لممارسة حقوقك، يمكنك التواصل معنا عبر البريد الإلكتروني support@pass-tickets.com أو عن طريق الهاتف 920025515.


`
                  : `
                        
## Privacy Policy and Notice

At PassTickets, your privacy matters. This document explains how we handle personal data on our websites, applications, and digital platforms. It combines our Privacy Policy and Privacy Notice to provide clarity and comply with applicable data protection laws, including Saudi Arabia's Personal Data Protection Law (PDPL) and other relevant regulations. By using our platforms, you agree to this document's terms. If you have questions, feel free to contact us.

### Who We Are

PassTickets ("we," "us," or "our") is a company registered with the Ministry of Commerce in Saudi Arabia. We provide services related to entertainment and tourism activities in the Kingdom. As a "data controller," we determine how personal data is collected, processed, and shared, ensuring compliance with legal and ethical standards.

### What Personal Data Do We Collect?

When you interact with us, we may collect personal data, including your full name, mobile number, email address, gender, country, and nationality. Depending on your interactions, we may also collect location data based on device settings, booking details such as event date, ticket types, number of tickets, and payment details like card type, transaction date, and payment time. Engagement data, including the number of interactions with our platform, technical data such as your IP address, browser type, device type, and time zone, along with promotional preferences, usage data about platform activity, and browsing history, may also be collected. Additionally, we may gather information such as feedback, survey responses, preferences, customer support interactions, and analytics to improve our services.

### Why Do We Collect Your Data?

We collect your personal data to deliver requested services, improve user experience, send updates or promotions with consent, and comply with legal obligations. Our aim is to provide exceptional customer service while ensuring transparency and protecting your data.

### Your Rights

Under the PDPL (Personal Data Protection Law) and applicable laws, you have the right to access your data, request corrections for inaccurate or incomplete information, request deletion of your data when legally permissible, and object to certain uses of your data.

### Data Sharing

We may share your data with service providers for payment processing or service delivery and with partners for co-branded events or promotions when you provide consent.

  

### Data Security

We employ technical and administrative measures to safeguard your personal data against unauthorized access, loss, or misuse.

### Data Retention

Your data is retained only as long as necessary for its intended purposes or as required by law.

### Automated Decision-Making and Analytics

PassTickets does not process your data using automated decision-making or profiling. However, we use analytics to improve our platforms and may display targeted advertisements with your consent. For further details, refer to our Cookie Policy.

### Legal Basis for Data Processing

We process your data to fulfill contractual obligations, comply with legal requirements, and improve our services based on legitimate interests.

### Changes in Ownership

If PassTickets undergoes a business change, such as a sale or merger, your data may be transferred to the new entity, which will honor this policy and notice.

### Updates to This Document

This document may be updated to reflect legal or operational changes, and significant updates will be communicated through email, notifications, or banners on our platforms.

### Disclaimer of Liability

PassTickets is not responsible for direct, indirect, incidental, or consequential damages arising from your use or inability to use our platforms.

### How to Contact Us

For inquiries or to exercise your rights, you can reach us via email at support@pass-tickets.com or by phone at 920025515.

                        `
              }
            />
          </div>
        </div>
      </div>
    </SupportLayout>
  );
};

export default PrivacyPolicy;
